import type { GameState, Player, BoardModifiers } from './types';
import { appendEvent } from './event-log';
import { modifyKeys } from './economy';
import { addDroppedKeys, resolveDroppedKeyPickupAtNode } from './dropped-keys';
import { tryBlockWithStoneSkin } from './stone-skin-block';
import { ensurePlayerStats } from './stats';

/**
 * Maximum HP for a player. Used in damage ratio calculations.
 * Spec §3.1: Player.hp max is 30.
 */
const MAX_HP = 30;

/**
 * Calculate the number of keys a player drops from damage.
 *
 * Rules:
 * - If player has 0 keys → returns 0 regardless of modifiers.
 * - Pinata modifier: ALL keys drop (both lethal and non-lethal).
 * - Lethal (isLethal = true): 30% of keys, capped at 30.
 * - Non-lethal: proportional to damage ratio — floor(damage / MAX_HP * keys).
 *
 * @param player    - The player taking damage (reads .keys only; does NOT mutate).
 * @param damage    - The effective damage amount applied.
 * @param isLethal  - Whether the damage kills the player (hp → 0).
 * @param modifiers - Active board modifier flags.
 */
export function calculateKeyDrop(
  player: Player,
  damage: number,
  isLethal: boolean,
  modifiers: BoardModifiers,
): number {
  if (player.keys <= 0) return 0;
  if (modifiers.pinata) return player.keys; // Pinata: ALL keys drop
  if (isLethal) return Math.min(Math.floor(player.keys * 0.3), 30);
  return Math.floor((damage / MAX_HP) * player.keys);
}

/**
 * Remove keys from a player using the damage-drop rules and place them on
 * their current tile. This is intentionally separate from HP damage so only
 * mechanics that explicitly call it can cause key loss.
 */
export function dropKeysFromDamage(
  state: GameState,
  playerId: string,
  damage: number,
  isLethal: boolean,
): number {
  const player = state.players.get(playerId);
  if (!player) throw new Error(`Player not found: ${playerId}`);

  const requestedKeysDropped = calculateKeyDrop(player, damage, isLethal, state.boardModifiers);
  if (requestedKeysDropped <= 0) return 0;

  const keysDropped = Math.abs(
    modifyKeys(state, playerId, -requestedKeysDropped, isLethal ? 'death_drop' : 'damage_drop'),
  );
  if (keysDropped > 0) {
    addDroppedKeys(state, player.nodeId, keysDropped, playerId);
  }
  return keysDropped;
}

type DamageKeyDropHandler = (effectiveAmount: number, isLethal: boolean) => number;

export interface DamageResult {
  died: boolean;
  keysDropped: number;
  blockedByStoneSkin?: boolean;
}

function canonicalStoneSkinSourceId(source: string): string {
  if (source === 'npc_shadow_stalker') return 'shadow_stalker';
  if (source.startsWith('item_')) return source.slice('item_'.length);
  return source;
}

function applyDamageInternal(
  state: GameState,
  playerId: string,
  amount: number,
  source: string,
  sourcePlayerId: string | null,
  keyDropHandler: DamageKeyDropHandler | null,
): DamageResult {
  const player = state.players.get(playerId);
  if (!player) throw new Error(`Player not found: ${playerId}`);

  // No-op: player is already dead
  if (player.isDead) return { died: false, keysDropped: 0 };
  if (state.godModeImmunity?.get(playerId)?.noDamage === true) {
    return { died: false, keysDropped: 0 };
  }

  if (
    amount > 0 &&
    source !== 'soul_duel_backfire' &&
    tryBlockWithStoneSkin(state, playerId, {
      actorId: sourcePlayerId,
      sourceId: canonicalStoneSkinSourceId(source),
    })
  ) {
    return { died: false, keysDropped: 0, blockedByStoneSkin: true };
  }

  // One Hit Kill modifier: override damage to MAX_HP
  let effectiveAmount = amount;
  if (state.boardModifiers.oneHitKill) {
    effectiveAmount = MAX_HP;
    appendEvent(state, {
      type: 'modifier_triggered',
      playerId: null,
      modifier: 'one_hit_kill',
      detail: `Damage overridden to ${MAX_HP}`,
    });
  }

  // Apply HP reduction
  const newHp = Math.max(0, player.hp - effectiveAmount);
  player.hp = newHp;
  const isLethal = newHp <= 0;

  // Emit damage event
  appendEvent(state, {
    type: 'damage',
    playerId,
    sourcePlayerId,
    amount: effectiveAmount,
    source,
    newHp,
  });

  // Stats tracking
  const victimStats = ensurePlayerStats(state, playerId);
  victimStats.damageReceived += effectiveAmount;
  if (sourcePlayerId !== null) {
    const attackerStats = ensurePlayerStats(state, sourcePlayerId);
    attackerStats.damageDealt += effectiveAmount;
  }

  const keysDropped = keyDropHandler?.(effectiveAmount, isLethal) ?? 0;

  // Handle death
  if (isLethal) {
    player.isDead = true;
    appendEvent(state, {
      type: 'death',
      playerId,
      killerPlayerId: sourcePlayerId,
      cause: source,
      keysDropped,
    });
  }

  if (keysDropped > 0) {
    resolveDroppedKeyPickupAtNode(state, player.nodeId);
  }

  return { died: isLethal, keysDropped };
}

/**
 * Apply damage to a player.
 *
 * Effects (in order):
 * 1. No-op if player is already dead → returns { died: false, keysDropped: 0 }.
 * 2. One Hit Kill modifier: overrides damage to MAX_HP, emits modifier_triggered event.
 * 3. Subtracts HP (clamped to 0). Emits damage event.
 * 4. Updates damageReceived on victim and damageDealt on source (if sourcePlayerId is non-null).
 * 5. If lethal: sets player.isDead = true, emits death event.
 *
 * @param state           - Game state to mutate.
 * @param playerId        - ID of the player taking damage.
 * @param amount          - Damage amount (may be overridden by oneHitKill modifier).
 * @param source          - Human-readable damage source (e.g. 'hazard_tile', 'item_thunder_fist').
 * @param sourcePlayerId  - Player who dealt the damage, or null for environment.
 * @returns { died, keysDropped }
 * @throws Error if playerId is not found in state.players.
 */
export function applyDamage(
  state: GameState,
  playerId: string,
  amount: number,
  source: string,
  sourcePlayerId: string | null,
  suppressKeyDrop: boolean = false,
): DamageResult {
  return applyDamageInternal(
    state,
    playerId,
    amount,
    source,
    sourcePlayerId,
    suppressKeyDrop
      ? null
      : (effectiveAmount, isLethal) =>
          isLethal ? dropKeysFromDamage(state, playerId, effectiveAmount, true) : 0,
  );
}

/**
 * Apply HP damage and explicitly drop keys using damage-drop rules.
 * Use this only for mechanics whose rules include key loss, such as hazard tiles.
 */
export function applyDamageWithKeyDrop(
  state: GameState,
  playerId: string,
  amount: number,
  source: string,
  sourcePlayerId: string | null,
): DamageResult {
  return applyDamageInternal(
    state,
    playerId,
    amount,
    source,
    sourcePlayerId,
    (effectiveAmount, isLethal) => dropKeysFromDamage(state, playerId, effectiveAmount, isLethal),
  );
}
